import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useContextPressure, type ContextPressure } from "@/features/admin";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageLayout } from "../layout";
import { StatCard } from "./components/stat-card";

const WINDOWS = [
  { hours: 24, label: "24 dernières heures" },
  { hours: 24 * 7, label: "7 derniers jours" },
  { hours: 24 * 30, label: "30 derniers jours" },
];

const tokens = (n: number) =>
  n >= 1000 ? `~${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)} k` : `~${n}`;

const pct = (n: number) => `${Math.round(n * 100)} %`;

const when = (t: number) =>
  t
    ? new Date(t).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** Red past the strictest window, amber past its near-limit share. */
const toneOf = (
  value: number,
  data: ContextPressure | undefined,
): "default" | "warn" | "bad" => {
  if (!data?.windows.length) return "default";
  const strictest = Math.min(...data.windows.map((w) => w.tokens));
  if (value >= strictest) return "bad";
  if (value >= strictest * data.near_limit_ratio) return "warn";
  return "default";
};

/**
 * How full the talking models' context window gets — "are we close to what Haiku can take?".
 *
 * Every figure is an estimate written by the turn builder before the request leaves
 * (characters / 3.5 over the system prompt, the history sent, the tool definitions and the
 * message): an order of magnitude, deliberately on the high side, not the provider's count.
 */
export function AdminContextPressurePage() {
  const [hours, setHours] = useState(24 * 7);
  const { data, isLoading } = useContextPressure(hours);
  const totals = data?.totals;
  const breakdown = data?.breakdown;
  const breakdownTotal = breakdown
    ? breakdown.system_tokens +
      breakdown.history_tokens +
      breakdown.tools_tokens +
      breakdown.message_tokens
    : 0;
  const histogramMax = Math.max(
    1,
    ...(data?.histogram || []).map((b) => b.turns),
  );

  return (
    <AdminPageLayout
      title="Fenêtre de contexte"
      description="Taille estimée de ce que chaque tour envoie au modèle, comparée à sa fenêtre"
      actions={[
        <Select
          key="window"
          value={String(hours)}
          onValueChange={(v) => setHours(Number(v))}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOWS.map((w) => (
              <SelectItem key={w.hours} value={String(w.hours)}>
                {w.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>,
      ]}
    >
      <p className="text-xs text-muted-foreground">
        Estimation : nombre de caractères ÷ 3,5 sur le prompt système,
        l'historique envoyé (résultats d'outils compris), la définition des
        outils et le message. Un ordre de grandeur volontairement pessimiste,
        pas le décompte du fournisseur.
      </p>

      <div className="grid grid-cols-4 gap-4">
        <StatCard
          title="Médiane par tour"
          value={totals ? tokens(totals.p50_tokens) : "—"}
          hint={
            totals ? `${totals.turns.toLocaleString("fr-FR")} tours` : undefined
          }
          isLoading={isLoading}
          tone={toneOf(totals?.p50_tokens || 0, data)}
        />
        <StatCard
          title="p95 par tour"
          value={totals ? tokens(totals.p95_tokens) : "—"}
          isLoading={isLoading}
          tone={toneOf(totals?.p95_tokens || 0, data)}
        />
        <StatCard
          title="Plus gros tour"
          value={totals ? tokens(totals.max_tokens) : "—"}
          hint={totals ? `${totals.sessions} conversation(s)` : undefined}
          isLoading={isLoading}
          tone={toneOf(totals?.max_tokens || 0, data)}
        />
        <StatCard
          title="Historique tronqué"
          value={totals ? totals.truncated_turns.toLocaleString("fr-FR") : "—"}
          hint="Tours où les plus anciens messages n'étaient plus envoyés"
          isLoading={isLoading}
          tone={totals?.truncated_turns ? "warn" : "default"}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Par rapport à la fenêtre du modèle
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Proche = au-delà de {data ? pct(data.near_limit_ratio) : "75 %"}{" "}
              de la fenêtre.
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Modèle</TableHead>
                  <TableHead className="text-right">Fenêtre</TableHead>
                  <TableHead className="text-right">Tours proches</TableHead>
                  <TableHead className="text-right">Tours au-delà</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.windows || []).map((w) => (
                  <TableRow key={w.key}>
                    <TableCell className="font-medium">{w.label}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {tokens(w.tokens)}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${
                        w.near ? "text-amber-600 dark:text-amber-400" : ""
                      }`}
                    >
                      {w.near || "—"}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${
                        w.over ? "text-red-600 dark:text-red-400" : ""
                      }`}
                    >
                      {w.over || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Ce qui remplit la fenêtre</CardTitle>
            <p className="text-xs text-muted-foreground">Moyenne par tour.</p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                {breakdown
                  ? (
                      [
                        ["Prompt système", breakdown.system_tokens],
                        [
                          "Historique (outils compris)",
                          breakdown.history_tokens,
                        ],
                        ["Définition des outils", breakdown.tools_tokens],
                        ["Message", breakdown.message_tokens],
                      ] as const
                    ).map(([label, value]) => (
                      <TableRow key={label}>
                        <TableCell>{label}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {tokens(value)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {breakdownTotal ? pct(value / breakdownTotal) : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Répartition des tours</CardTitle>
        </CardHeader>
        <CardContent>
          {(data?.histogram || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucun tour mesuré sur la période.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Taille estimée</TableHead>
                  <TableHead className="text-right">Tours</TableHead>
                  <TableHead className="w-1/2" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.histogram.map((b) => (
                  <TableRow key={b.from_tokens}>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {tokens(b.from_tokens)} – {tokens(b.to_tokens)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {b.turns}
                    </TableCell>
                    <TableCell>
                      <div
                        className="h-2 rounded bg-primary/60"
                        style={{ width: `${(b.turns / histogramMax) * 100}%` }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Conversations les plus chargées
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data?.sessions || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune donnée.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Conversation</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Tours</TableHead>
                  <TableHead className="text-right">Plus gros tour</TableHead>
                  <TableHead className="text-right">Messages envoyés</TableHead>
                  <TableHead className="text-right">Dernier tour</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data!.sessions.map((s) => (
                  <TableRow key={s.session_id}>
                    <TableCell className="font-mono text-xs">
                      <Link
                        className="underline-offset-2 hover:underline"
                        to={`/admin/explorer/sessions/${encodeURIComponent(s.session_id)}`}
                      >
                        {s.session_id}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      <Link
                        className="underline-offset-2 hover:underline"
                        to={`/admin/clients/${encodeURIComponent(s.client_id)}`}
                      >
                        {s.client_id}
                      </Link>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.turns}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${
                        toneOf(s.max_tokens, data) === "bad"
                          ? "text-red-600 dark:text-red-400"
                          : toneOf(s.max_tokens, data) === "warn"
                            ? "text-amber-600 dark:text-amber-400"
                            : ""
                      }`}
                    >
                      {tokens(s.max_tokens)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.max_history_messages}
                      {s.truncated ? (
                        <span className="ml-1 text-amber-600 dark:text-amber-400">
                          (tronqué)
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                      {when(s.last_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
